import {
    Component,
    ViewEncapsulation,
    OnInit,
    Inject,
    Optional,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../../lib/api.service';
import { LogService } from '../../../lib/log.service';
import { ModalService } from '../../../lib/modal.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Assessment, Question, Option } from '../../../models/assessment-admin';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AnalyticEvent } from 'app/lib/analytic-event';

@Component({
    selector: 'app-assessment-edit',
    templateUrl: './assessments-edit.component.html',
    styleUrls: ['./assessments-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class AssessmentEditComponent implements OnInit {
    assessmentEditForm: FormGroup;
    assessmentId: number;
    courseKey: string;
    title: string;
    mode: string;
    assessment: Assessment;
    isLoaded: boolean = false;
    questionTypes: Array<object>;
    submitButtonPressed: boolean = false;
    errors: string = '';
    showNumberValidation: boolean[] = [];

    constructor(
        private translate: TranslateService,
        private formBuilder: FormBuilder,
        private api: ApiService,
        private log: LogService,
        private modalService: ModalService,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location
    ) {
        const id = this.route.snapshot.paramMap.get('id');
        this.assessmentId = id ? parseInt(id, 10) : null;
        if (this.assessmentId) {
            this.title = 'Edit this Assessment';
            this.mode = 'update';
        } else {
            this.assessment = new Assessment({});
            this.title = 'Create an Assessment';
            this.mode = 'create';
        }

        this.createAssessmentEditForm(new Assessment({}));
    }

    closeModal() {}

    ngOnInit() {
        this.questionTypes = [
            { value: 1, label: 'One answer' },
            { value: 2, label: 'Multiple answers' },
            { value: 3, label: 'Resource Yes/No' },
            { value: 4, label: 'Dropdown' },
            { value: 5, label: 'TextArea' },
            { value: 6, label: 'TextBox' },
            { value: 7, label: 'Nps Slider' },
            { value: 8, label: 'Number' },
            { value: 9, label: 'Date of birth' },
        ];

        this.isLoaded = true;

        if (this.assessmentId) {
            this.isLoaded = false;
            this.api.get('assessment/listing/' + this.assessmentId).subscribe(
                (result: any) => {
                    this.assessment = new Assessment(result.data);
                    this.createAssessmentEditForm(this.assessment);
                    this.assessment.questions.forEach((element, index) => {
                        if (element.type === 8) {
                            this.onTypeChange(element.type, index);
                        }
                    });
                    this.isLoaded = true;
                },
                (error: any) => {
                    this.log.error(
                        'Error getting assessment. ' + error.message
                    );
                    this.isLoaded = true;
                }
            );
        }
    }

    get f() {
        return this.assessmentEditForm.controls;
    }

    createAssessmentEditForm(data: any) {
        this.assessmentEditForm = this.formBuilder.group({
            id: [data.id || null],
            quizType: [data.quizType || '', [Validators.required]],
            comment: [data.comment || '', [Validators.required]],
            questions: this.formBuilder.array([]),
            default: [data.default || false],
            active: [data.active || false],
        });

        data.questions?.forEach((question: any) => {
            this.addQuestion(question);
        });
    }

    onTypeChange(event: any, index: number) {
        const selectedValue = event;
        this.showNumberValidation[index] = selectedValue === 8;
    }

    get questions() {
        return this.assessmentEditForm.get('questions') as FormArray;
    }

    getQuestion(index: number) {
        const revisionsForm = this.assessmentEditForm.get(
            'questions'
        ) as FormArray;
        const formGroup = revisionsForm.controls[index] as FormGroup;
        return formGroup;
    }

    getOption(i: number, r: number) {
        const questionFormArray = this.assessmentEditForm.get(
            'questions'
        ) as FormArray;
        const questionFormGroup = questionFormArray.controls[i] as FormGroup;
        const optionFormArray = questionFormGroup.get('options') as FormArray;
        const optionFormGroup = optionFormArray.controls[r] as FormGroup;
        return optionFormGroup;
    }

    onClose() {
        this.location.back();
    }

    addQuestion(data: any) {
        this.assessment.questions.push(new Question({}));

        const questionList = this.assessmentEditForm.get(
            'questions'
        ) as FormArray;
        questionList.push(this.createQuestion(data));

        if (data.options) {
            data.options?.forEach((option: any) => {
                this.addOption(questionList.length - 1, option);
            });
        }
    }

    createQuestion(data: any) {
        return this.formBuilder.group({
            id: [data.id || null],
            quizId: [data.quizId || null],
            comment: [data.comment || '', [Validators.required]],
            sortOrder: [data.sortOrder || null],
            tabulate: [data.tabulate || null],
            group: [data.group || ''],
            required: [data.required || false, []],
            type: [data.type || null],
            active: [data.active || null],
            options: this.formBuilder.array([]),
            validations: [data.validations],
            min: [data.min || 0],
            max: [data.max || 0],
        });
    }

    createValidationData(data: any) {
        return this.formBuilder.group({
            min: [data.validations?.min?.min || null],
            max: [data.validations?.max?.max || null],
        });
    }

    onRemoveQuestion(i: number) {
        this.assessment.questions.splice(i, 1);

        const assessmentList = this.assessmentEditForm.get(
            'questions'
        ) as FormArray;
        assessmentList.removeAt(i);
    }

    addOption(i: number, data: any) {
        this.assessment.questions[i].options.push(new Option({}));

        const questionList = this.assessmentEditForm.get(
            'questions'
        ) as FormArray;
        const optionList = questionList.at(i).get('options') as FormArray;
        optionList.push(this.createOption(data));
    }

    createOption(data: any) {
        return this.formBuilder.group({
            id: [data.id || null],
            questionId: [data.questionId || null],
            optionValue: [
                data.optionValue === 0 ? 0 : data.optionValue || null,
            ],
            sortOrder: [data.sortOrder === 0 ? 0 : data.sortOrder || ''],
            comment: [data.comment || '', [Validators.required]],
        });
    }

    removeOption(i: number, r: number) {
        this.assessment.questions[i].options.splice(r, 1);

        const questionList = this.assessmentEditForm.get(
            'questions'
        ) as FormArray;
        const optionsList = questionList.at(i).get('options') as FormArray;
        optionsList.removeAt(r);
    }

    getOptions(i: number) {
        const questionList = this.assessmentEditForm.get(
            'questions'
        ) as FormArray;
        const optionList = questionList.at(i).get('options') as FormArray;
        return optionList;
    }

    doSave() {
        this.errors = '';
        this.assessment = new Assessment(this.assessmentEditForm.value);
        this.submitButtonPressed = true;
        if (
            !this.assessment.questions ||
            this.assessment.questions.length === 0
        ) {
            this.errors = 'The assessment must have atleast one question';
            return;
        }

        let questionPass = this.assessment.questions.every(
            (question, index) => {
                this.log.debug('-----------', question);
                if (!question.comment) {
                    this.errors = `The comment field of question ${index + 1} is empty!`;
                    return false;
                }
                if (
                    question.type !== 5 &&
                    question.type !== 6 &&
                    !(question.options || question.options.length === 0)
                ) {
                    this.errors = `The question ${index + 1} has no options!`;
                    return false;
                }

                return question.options.every((option, optionIndex) => {
                    if (!option.comment) {
                        this.errors = `The option ${optionIndex + 1} of question ${index + 1} is empty!`;
                        return false;
                    }
                    return true;
                });
            }
        );
        if (!this.assessmentEditForm.valid) {
            return;
        }

        if (!questionPass) {
            return;
        }

        this.errors = '';

        if (this.mode === 'update') {
            this.api
                .put(
                    'assessment/listing/' + this.assessment.id,
                    this.assessment
                )
                .subscribe(
                    (data: any) => {
                        this.log.event(AnalyticEvent.event.assessmentUpdated);
                        this.finishSave('Assessment has been updated', data);
                    },
                    (error: any) => {
                        this.modalService.showAlert(
                            'Error',
                            'Something went wrong. Please try again'
                        );
                    }
                );
        } else {
            for (let obj of this.assessment.questions) {
                delete obj.min;
                delete obj.max;
                obj.validations = JSON.stringify(obj.validations);
            }

            this.api.post('assessment/listing', this.assessment).subscribe(
                (data: any) => {
                    this.log.event(AnalyticEvent.event.assessmentCreated);
                    this.finishSave('Assessment has been created', data);
                },
                (error: any) => {
                    this.modalService.showAlert(
                        'Error',
                        'Something went wrong. Please try again'
                    );
                }
            );
        }
    }

    /**
     * Common save-completion path. Closes the modal (passing the
     * server's response so the parent listing can refresh) when opened
     * as a dialog; otherwise falls back to the legacy route navigation.
     * Both paths show a success toast.
     */
    private finishSave(successMessage: string, response: any) {
        this.modalService.showAlert('Success', successMessage);
        this.router.navigate(['/admin/assessments-listing']);
    }

    drop(event: CdkDragDrop<string[]>) {
        const questionList = this.assessmentEditForm.get(
            'questions'
        ) as FormArray;
        const question = questionList.at(event.previousIndex);
        questionList.removeAt(event.previousIndex);
        questionList.insert(event.currentIndex, question);
    }

    dropItem(event: CdkDragDrop<string[]>) {
        moveItemInArray(
            event.container.data,
            event.previousIndex,
            event.currentIndex
        );
    }
}
